import {
  estimateAlignment,
  AlignmentInputError,
  MIN_CORRESPONDENCES,
  type AlignmentEstimate,
  type AlignmentWarning,
  type CorrespondencePoint,
  type ModelComparisonPresentationSummary,
} from "@voxelspy/analysis";
import { IDENTITY_MAT4, type Mat4, type Report } from "@voxelspy/contracts";
import { SessionArchiveError } from "@voxelspy/session-archive";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import {
  AlignmentCancelledError,
  estimateIcpAlignmentAsync,
} from "./alignment-worker-client";
import {
  estimateAnalysisFit,
  evaluateCapabilityPreflight,
  readEnvironmentReadings,
  type CapabilityPreflight,
} from "./capability";
import {
  ACCEPTED_UPLOAD_ACCEPT,
  defaultFrameForFormat,
  formatDeclaresOwnFrame,
  formatFrameDeclarationSummary,
  hasAcceptedExtension,
  inferFormat,
  unsupportedFormatMessage,
  type ResolvedSourceAxis,
  type ResolvedSourceUnit,
} from "./formats";
import {
  boundedEntityId,
  brandId,
  buildComparisonReport,
  renderReportHtml,
  ReportBuildError,
} from "./report";
import {
  asArrayBufferBacked,
  describeSessionError,
  openSession,
  saveSession,
  sessionImportSpecFor,
  slug,
  SESSION_FILE_MEDIA_TYPE,
  type SavedSession,
  type SessionSourceModels,
} from "./session";
import {
  ANALYSIS_MEMORY_MAX_MIB,
  ANALYSIS_MEMORY_MIN_MIB,
  ANALYSIS_MEMORY_STEP_MIB,
  ComparisonCancelledError,
  DEFAULT_ANALYSIS_MEMORY_MIB,
  reimportSessionModels,
  runComparison,
  type ComparisonProgress,
  type ComparisonSource,
  type CompletedComparison,
} from "./worker-client";

const Workbench = lazy(async () => {
  const module = await import("./Workbench");
  return { default: module.Workbench };
});

const units: { value: ResolvedSourceUnit; label: string }[] = [
  { value: "millimetre", label: "Millimetres" },
  { value: "centimetre", label: "Centimetres" },
  { value: "metre", label: "Metres" },
  { value: "micrometre", label: "Micrometres" },
  { value: "inch", label: "Inches" },
  { value: "foot", label: "Feet" },
];
const axes: { value: ResolvedSourceAxis; label: string }[] = [
  { value: "right-handed-z-up", label: "Right-handed, Z up" },
  { value: "right-handed-y-up", label: "Right-handed, Y up" },
];

type SourceSelection = {
  file: File | null;
  unit: ResolvedSourceUnit | "";
  axis: ResolvedSourceAxis | "";
  frameSource: "default" | "expert";
};

export function sourceSelectionForFile(file: File | null): SourceSelection {
  const format = file ? inferFormat(file.name) : undefined;
  return { file, ...defaultFrameForFormat(format), frameSource: "default" };
}

const emptySource = (): SourceSelection => sourceSelectionForFile(null);

/**
 * Server-side prerendering (and the first client render, before hydration
 * settles) has no device signals to read, so this matches exactly what
 * `evaluateCapabilityPreflight` returns for an all-unknown, worker-capable
 * reading -- optimistic about Worker/WebGL support (the overwhelmingly
 * common case) and at today's existing default allowance. The real
 * device-derived preflight replaces this after mount (see the effect in
 * `ComparisonFlow`), which keeps prerendered HTML and the first client
 * render identical and avoids a hydration mismatch.
 */
const INITIAL_CAPABILITY: CapabilityPreflight = {
  recommendedAnalysisMemoryMiB: DEFAULT_ANALYSIS_MEMORY_MIB,
  memoryNotes: [],
  workersAvailable: true,
  webglAvailable: true,
  analysisSupported: true,
  blockingMessage: undefined,
};

export function sourceCapability(selection: SourceSelection) {
  if (!selection.file)
    return { ready: false, message: "Choose a local model file." };
  if (!hasAcceptedExtension(selection.file.name))
    return { ready: false, message: unsupportedFormatMessage() };
  if (selection.file.size === 0)
    return { ready: false, message: "The selected file is empty." };
  if (selection.file.size > 32 * 1024 * 1024)
    return {
      ready: false,
      message: "The selected file exceeds the 32 MiB importer safety ceiling.",
    };
  const declaresOwnFrame = formatDeclaresOwnFrame(
    inferFormat(selection.file.name),
  );
  if (!declaresOwnFrame && (!selection.unit || !selection.axis))
    return {
      ready: false,
      message:
        "Choose the source unit and up-axis; this format does not declare them authoritatively.",
    };
  if (declaresOwnFrame)
    return {
      ready: true,
      message:
        selection.unit || selection.axis
          ? "Ready for local comparison using the selected override source frame."
          : "Ready for local comparison using this file's own declared source frame.",
    };
  return {
    ready: true,
    message:
      selection.frameSource === "default"
        ? "Ready for local comparison using millimetres and right-handed Z-up."
        : "Ready for local comparison using the selected expert source frame.",
  };
}

function SourceCard({
  role,
  selection,
  update,
}: {
  role: "Baseline" | "Candidate";
  selection: SourceSelection;
  update: (selection: SourceSelection) => void;
}) {
  const capability = sourceCapability(selection);
  const id = role.toLocaleLowerCase("en-US");
  const format = selection.file ? inferFormat(selection.file.name) : undefined;
  const declaresOwnFrame = formatDeclaresOwnFrame(format);
  return (
    <fieldset className="source-card">
      <legend>{role}</legend>
      <label className="source-file" htmlFor={`${id}-file`}>
        <span>{selection.file?.name ?? "Choose a model"}</span>
        <small>
          {selection.file
            ? `${(selection.file.size / 1024).toFixed(1)} KiB · local file`
            : "STL, OBJ, glTF, GLB, or 3MF, up to 32 MiB"}
        </small>
        <input
          id={`${id}-file`}
          type="file"
          accept={ACCEPTED_UPLOAD_ACCEPT}
          onChange={(event) =>
            update(
              sourceSelectionForFile(event.currentTarget.files?.[0] ?? null),
            )
          }
        />
        <span className="button button-secondary" aria-hidden="true">
          Browse this device
        </span>
      </label>
      <details>
        <summary>Expert settings</summary>
        <p>
          {declaresOwnFrame
            ? formatFrameDeclarationSummary(format)
            : "Change these only when the source uses a different unit or up-axis. The selected values are recorded with the comparison."}
        </p>
        <div className="source-frame">
          <label>
            Source unit
            <select
              value={selection.unit}
              onChange={(event) =>
                update({
                  ...selection,
                  unit: event.currentTarget.value as SourceSelection["unit"],
                  frameSource: "expert",
                })
              }
            >
              {declaresOwnFrame && (
                <option value="">Use the file&rsquo;s declared value</option>
              )}
              {units.map((unit) => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Source up-axis
            <select
              value={selection.axis}
              onChange={(event) =>
                update({
                  ...selection,
                  axis: event.currentTarget.value as SourceSelection["axis"],
                  frameSource: "expert",
                })
              }
            >
              {declaresOwnFrame && (
                <option value="">Use the file&rsquo;s declared value</option>
              )}
              {axes.map((axis) => (
                <option key={axis.value} value={axis.value}>
                  {axis.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </details>
      <p
        className={
          capability.ready ? "capability capability-ready" : "capability"
        }
      >
        <i />
        {capability.message}
      </p>
    </fieldset>
  );
}

/** Downloads a saved session as a `.voxelspy` file via a revocable Blob URL. */
function downloadSessionArchive(saved: SavedSession) {
  const blob = new Blob([asArrayBufferBacked(saved.bytes)], {
    type: SESSION_FILE_MEDIA_TYPE,
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = saved.fileName;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoking synchronously can race the download the click() just started in
  // some browsers; a 0ms macrotask lets that navigation begin first.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function describeSessionFailure(reason: unknown, action: "open" | "save") {
  if (reason instanceof SessionArchiveError)
    return describeSessionError(reason);
  if (reason instanceof Error) return reason.message;
  return action === "open"
    ? "The session could not be opened safely."
    : "The session could not be saved safely.";
}

/** File name for a downloaded report export: mirrors `sessionFileName`'s convention. */
function reportFileName(report: Report): string {
  const baseline = report.models.find((model) => model.role === "baseline");
  const candidate = report.models.find((model) => model.role === "candidate");
  return `voxelspy-report-${slug(baseline?.sourceName)}-vs-${slug(candidate?.sourceName)}.html`;
}

/** Downloads a rendered report as a self-contained `.html` file via a revocable Blob URL. */
function downloadReportHtml(report: Report, html: string) {
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = reportFileName(report);
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // See `downloadSessionArchive` above for why the revoke is deferred.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function describeReportFailure(reason: unknown): string {
  if (reason instanceof ReportBuildError) return reason.message;
  if (reason instanceof Error) return reason.message;
  return "The report could not be built safely.";
}

// ---------------------------------------------------------------------------
// Alignment (opt-in, off by default). A user may enter at least
// `MIN_CORRESPONDENCES` numeric correspondence-point pairs -- candidate
// ("moving") coordinates paired with baseline ("fixed") coordinates -- and
// review `estimateAlignment`'s (`@voxelspy/analysis`) closed-form rigid fit
// before optionally refining it with iterative-closest-point using the
// already-imported baseline/candidate geometry. Nothing here ever runs
// automatically: `estimateAlignment` only ever returns a transform plus its
// evidence for review; only this module's explicit "Accept this alignment"
// action (see `acceptAlignment` below) can make a transform part of a
// comparison request, and only as the candidate's `modelToComparison` --
// the same per-model placement field every comparison already carries, per
// AGENTS.md's "never silently recenter, rescale, align, repair, or
// reinterpret geometry" rule.
// ---------------------------------------------------------------------------

/**
 * What, if anything, a completed comparison's `candidate.modelToComparison`
 * reflects. `"with-evidence"` is set when this session itself computed and
 * accepted the alignment (its full `AlignmentEvidence` is still in memory).
 * `"transform-only"` is set when a reopened saved session's analysis result
 * already carries a non-identity candidate placement: the transform itself
 * round-trips through the session archive (it lives in the same
 * `modelToComparison` field every analysis request/result already carries),
 * but the alignment evidence that produced it (iterations, residuals,
 * warnings) is not part of the session or report contract and is not
 * recoverable after reopening -- this variant is how that honest limit is
 * surfaced rather than papered over.
 */
type AppliedAlignmentInfo =
  | { readonly kind: "with-evidence"; readonly estimate: AlignmentEstimate }
  | { readonly kind: "transform-only"; readonly transform: Mat4 };

interface CorrespondenceRow {
  readonly key: number;
  readonly moving: [number, number, number];
  readonly fixed: [number, number, number];
}

function emptyCorrespondenceRow(key: number): CorrespondenceRow {
  return { key, moving: [NaN, NaN, NaN], fixed: [NaN, NaN, NaN] };
}

function initialCorrespondenceRows(): CorrespondenceRow[] {
  return Array.from({ length: MIN_CORRESPONDENCES }, (_unused, index) =>
    emptyCorrespondenceRow(index),
  );
}

export function correspondenceRowsReady(
  rows: readonly CorrespondenceRow[],
): boolean {
  return (
    rows.length >= MIN_CORRESPONDENCES &&
    rows.every(
      (row) =>
        row.moving.every((value) => Number.isFinite(value)) &&
        row.fixed.every((value) => Number.isFinite(value)),
    )
  );
}

export function correspondenceRowsToPoints(
  rows: readonly CorrespondenceRow[],
): CorrespondencePoint[] {
  return rows.map((row) => ({
    moving: [...row.moving] as CorrespondencePoint["moving"],
    fixed: [...row.fixed] as CorrespondencePoint["fixed"],
  }));
}

export function isIdentityTransform(matrix: readonly number[]): boolean {
  return (
    matrix.length === IDENTITY_MAT4.length &&
    matrix.every((value, index) => value === IDENTITY_MAT4[index])
  );
}

function describeAlignmentFailure(reason: unknown): string {
  if (reason instanceof AlignmentInputError) return reason.message;
  if (reason instanceof AlignmentCancelledError) return reason.message;
  if (reason instanceof Error) return reason.message;
  return "Alignment estimate failed safely.";
}

/**
 * A non-converged or poor-fit estimate -- or one carrying any warning --
 * must read as "review this", never as success: this is the single
 * predicate the evidence panel's styling and heading text below key off of,
 * so "looks fine" and "is actually flagged" can never silently diverge.
 */
export function alignmentNeedsReview(estimate: AlignmentEstimate): boolean {
  return (
    !estimate.evidence.converged ||
    estimate.evidence.poorFit ||
    estimate.warnings.length > 0
  );
}

function formatAlignmentNumber(value: number): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 6 }).format(
    value,
  );
}

function alignmentMethodLabel(
  method: AlignmentEstimate["evidence"]["method"],
): string {
  return method === "correspondence-points"
    ? "Correspondence points"
    : "Iterative closest point (refined)";
}

function AlignmentWarningList({
  warnings,
}: {
  warnings: readonly AlignmentWarning[];
}) {
  if (warnings.length === 0) return null;
  return (
    <ul className="alignment-warning-list">
      {warnings.map((warning) => (
        <li key={warning.code}>
          <strong>{warning.code}</strong>
          <p>{warning.message}</p>
        </li>
      ))}
    </ul>
  );
}

/**
 * Full, honest evidence for one `AlignmentEstimate`: every number
 * `AlignmentEvidence` carries, plus every warning, rendered before any
 * accept/discard choice is offered -- an estimate is never presented as if
 * it were already a confirmed result.
 */
function AlignmentEvidencePanel({ estimate }: { estimate: AlignmentEstimate }) {
  const needsReview = alignmentNeedsReview(estimate);
  const { evidence } = estimate;
  return (
    <div
      className={
        needsReview
          ? "alignment-evidence alignment-evidence-review"
          : "alignment-evidence"
      }
      role={needsReview ? "alert" : undefined}
    >
      <p className="alignment-evidence-heading">
        <strong>{alignmentMethodLabel(evidence.method)} estimate</strong>
        {needsReview
          ? " -- review before accepting."
          : " -- review the evidence below before accepting."}
      </p>
      <dl className="alignment-evidence-list">
        <div>
          <dt>Correspondences / samples</dt>
          <dd>{evidence.correspondenceCount}</dd>
        </div>
        <div>
          <dt>Iterations</dt>
          <dd>{evidence.iterations}</dd>
        </div>
        <div>
          <dt>Converged</dt>
          <dd>
            {evidence.converged ? "Yes" : "No -- reached its iteration ceiling"}
          </dd>
        </div>
        <div>
          <dt>Residual RMS, before / after</dt>
          <dd>
            {formatAlignmentNumber(
              evidence.residualsBeforeMillimetres.rmsMillimetres,
            )}{" "}
            mm /{" "}
            {formatAlignmentNumber(
              evidence.residualsAfterMillimetres.rmsMillimetres,
            )}{" "}
            mm
          </dd>
        </div>
        <div>
          <dt>Residual maximum, before / after</dt>
          <dd>
            {formatAlignmentNumber(
              evidence.residualsBeforeMillimetres.maxMillimetres,
            )}{" "}
            mm /{" "}
            {formatAlignmentNumber(
              evidence.residualsAfterMillimetres.maxMillimetres,
            )}{" "}
            mm
          </dd>
        </div>
        {evidence.impliedScale !== undefined && (
          <div>
            <dt>Implied scale (never applied)</dt>
            <dd>{formatAlignmentNumber(evidence.impliedScale)}&times;</dd>
          </div>
        )}
        <div>
          <dt>Fit quality</dt>
          <dd>
            {evidence.poorFit
              ? "Poor fit -- review before accepting"
              : "Within the poor-fit threshold"}
          </dd>
        </div>
      </dl>
      {evidence.poorFitReason && (
        <p className="alignment-evidence-reason">{evidence.poorFitReason}</p>
      )}
      <AlignmentWarningList warnings={estimate.warnings} />
      <p className="placement-transform">
        <strong>Estimated transform (not yet applied):</strong>{" "}
        <code>{estimate.transform.map(formatAlignmentNumber).join(" ")}</code>
      </p>
    </div>
  );
}

export function ComparisonFlow() {
  const [baseline, setBaseline] = useState(emptySource);
  const [candidate, setCandidate] = useState(emptySource);
  const [progress, setProgress] = useState<ComparisonProgress>();
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [result, setResult] = useState<CompletedComparison>();
  const [sourceModels, setSourceModels] = useState<SessionSourceModels>();
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "error">(
    "idle",
  );
  const [saveError, setSaveError] = useState<string>();
  const [exportStatus, setExportStatus] = useState<
    "idle" | "exporting" | "error"
  >("idle");
  const [exportError, setExportError] = useState<string>();
  const [openProgress, setOpenProgress] = useState<ComparisonProgress>();
  const [openError, setOpenError] = useState<string>();
  const [analysisMemoryMiB, setAnalysisMemoryMiB] = useState(
    DEFAULT_ANALYSIS_MEMORY_MIB,
  );
  const [capability, setCapability] =
    useState<CapabilityPreflight>(INITIAL_CAPABILITY);
  useEffect(() => {
    // Device signals (deviceMemory, hardwareConcurrency, pointer coarseness,
    // Worker/WebGL support) only exist client-side, so the recommended
    // allowance is applied once after mount rather than during the initial
    // render -- see `INITIAL_CAPABILITY` for why that keeps prerendered HTML
    // and the first client render identical. Running once on mount also
    // guarantees this never overwrites a value the user has since chosen on
    // the slider themselves.
    const preflight = evaluateCapabilityPreflight(readEnvironmentReadings());
    setCapability(preflight);
    setAnalysisMemoryMiB(preflight.recommendedAnalysisMemoryMiB);
  }, []);
  const activeRunRef = useRef<AbortController | null>(null);
  const activeOpenRunRef = useRef<AbortController | null>(null);
  const saveInFlightRef = useRef(false);
  const exportInFlightRef = useRef(false);
  const baselineCapability = useMemo(
    () => sourceCapability(baseline),
    [baseline],
  );
  const candidateCapability = useMemo(
    () => sourceCapability(candidate),
    [candidate],
  );
  const fitEstimate = useMemo(() => {
    if (!baseline.file || !candidate.file) return undefined;
    return estimateAnalysisFit(
      baseline.file.size + candidate.file.size,
      analysisMemoryMiB,
    );
  }, [baseline.file, candidate.file, analysisMemoryMiB]);
  const ready =
    baselineCapability.ready &&
    candidateCapability.ready &&
    !progress &&
    capability.analysisSupported;

  useEffect(() => {
    return () => {
      activeRunRef.current?.abort();
      activeOpenRunRef.current?.abort();
    };
  }, []);

  // --- Alignment (opt-in, off by default) -----------------------------
  const [alignmentEnabled, setAlignmentEnabled] = useState(false);
  const [correspondenceRows, setCorrespondenceRows] = useState<
    CorrespondenceRow[]
  >(initialCorrespondenceRows);
  const nextCorrespondenceKeyRef = useRef(MIN_CORRESPONDENCES);
  const [correspondenceError, setCorrespondenceError] = useState<string>();
  const [alignmentEstimate, setAlignmentEstimate] =
    useState<AlignmentEstimate>();
  const [icpBusy, setIcpBusy] = useState(false);
  const [icpError, setIcpError] = useState<string>();
  const [acceptedAlignment, setAcceptedAlignment] =
    useState<AlignmentEstimate>();
  const [appliedAlignment, setAppliedAlignment] =
    useState<AppliedAlignmentInfo>();
  const alignmentRunRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      alignmentRunRef.current?.abort();
    };
  }, []);

  const resetAlignmentState = () => {
    alignmentRunRef.current?.abort();
    alignmentRunRef.current = null;
    setAlignmentEnabled(false);
    setCorrespondenceRows(initialCorrespondenceRows());
    nextCorrespondenceKeyRef.current = MIN_CORRESPONDENCES;
    setCorrespondenceError(undefined);
    setAlignmentEstimate(undefined);
    setIcpBusy(false);
    setIcpError(undefined);
    setAcceptedAlignment(undefined);
  };

  const updateBaseline = (next: SourceSelection) => {
    setBaseline(next);
    resetAlignmentState();
  };
  const updateCandidate = (next: SourceSelection) => {
    setCandidate(next);
    resetAlignmentState();
  };

  const updateCorrespondencePoint = (
    key: number,
    side: "moving" | "fixed",
    axis: 0 | 1 | 2,
    value: number,
  ) => {
    setCorrespondenceRows((rows) =>
      rows.map((row) => {
        if (row.key !== key) return row;
        const nextPoint: [number, number, number] = [...row[side]];
        nextPoint[axis] = value;
        return { ...row, [side]: nextPoint };
      }),
    );
    setCorrespondenceError(undefined);
    setAlignmentEstimate(undefined);
    setIcpError(undefined);
    setAcceptedAlignment(undefined);
  };

  const addCorrespondenceRow = () => {
    setCorrespondenceRows((rows) => [
      ...rows,
      emptyCorrespondenceRow(nextCorrespondenceKeyRef.current++),
    ]);
    setAlignmentEstimate(undefined);
    setAcceptedAlignment(undefined);
  };

  const removeCorrespondenceRow = (key: number) => {
    setCorrespondenceRows((rows) =>
      rows.length > MIN_CORRESPONDENCES
        ? rows.filter((row) => row.key !== key)
        : rows,
    );
    setAlignmentEstimate(undefined);
    setAcceptedAlignment(undefined);
  };

  const estimateFromPoints = () => {
    setCorrespondenceError(undefined);
    setIcpError(undefined);
    setAcceptedAlignment(undefined);
    try {
      const correspondences = correspondenceRowsToPoints(correspondenceRows);
      const next = estimateAlignment({
        method: "correspondence-points",
        correspondences,
      });
      setAlignmentEstimate(next);
    } catch (reason) {
      setAlignmentEstimate(undefined);
      setCorrespondenceError(describeAlignmentFailure(reason));
    }
  };

  const canRefineWithIcp =
    Boolean(alignmentEstimate) &&
    !icpBusy &&
    baselineCapability.ready &&
    candidateCapability.ready &&
    capability.analysisSupported;

  const refineWithIcp = async () => {
    if (!alignmentEstimate || !baseline.file || !candidate.file) return;
    alignmentRunRef.current?.abort();
    const controller = new AbortController();
    alignmentRunRef.current = controller;
    setIcpBusy(true);
    setIcpError(undefined);
    try {
      const refined = await estimateIcpAlignmentAsync(
        {
          moving: {
            file: candidate.file,
            unit: candidate.unit,
            axis: candidate.axis,
            frameSource: candidate.frameSource,
          },
          fixed: {
            file: baseline.file,
            unit: baseline.unit,
            axis: baseline.axis,
            frameSource: baseline.frameSource,
          },
          initialTransform: alignmentEstimate.transform,
        },
        controller.signal,
      );
      setAlignmentEstimate(refined);
      setAcceptedAlignment(undefined);
    } catch (reason) {
      if (!(reason instanceof AlignmentCancelledError)) {
        setIcpError(describeAlignmentFailure(reason));
      }
    } finally {
      setIcpBusy(false);
      if (alignmentRunRef.current === controller)
        alignmentRunRef.current = null;
    }
  };

  const cancelIcpRefine = () => {
    alignmentRunRef.current?.abort();
  };

  const acceptAlignment = () => {
    if (!alignmentEstimate) return;
    setAcceptedAlignment(alignmentEstimate);
  };

  const discardAlignment = () => {
    setAcceptedAlignment(undefined);
  };

  /** Never applied unless the opt-in toggle is still on: unchecking it
   *  discards any previously accepted estimate from taking effect, even if
   *  the estimate itself is still shown for review. */
  const activeAlignment = alignmentEnabled ? acceptedAlignment : undefined;

  const compare = async () => {
    if (!ready || !baseline.file || !candidate.file) return;
    // A new run replaces any in-flight one; make sure it is stopped first.
    activeRunRef.current?.abort();
    activeOpenRunRef.current?.abort();
    const controller = new AbortController();
    activeRunRef.current = controller;
    setError(undefined);
    setNotice(undefined);
    setOpenError(undefined);
    setProgress({ stage: "starting", message: "Starting local comparison" });
    // Captured once at the start of this run: `activeAlignment` reflects
    // whatever was accepted (and the opt-in toggle still on) at the moment
    // "Validate and compare" was clicked, so a later edit to the alignment
    // section cannot retroactively change what this specific run used.
    const usedAlignment = activeAlignment;
    try {
      const next = await runComparison(
        baseline as ComparisonSource,
        candidate as ComparisonSource,
        setProgress,
        analysisMemoryMiB,
        controller.signal,
        usedAlignment?.transform,
      );
      // Re-read the original files (Blobs are immutable and freely
      // re-readable) so a portable session can be saved later without
      // holding the import's transferred bytes in memory the whole time.
      const [baselineBytes, candidateBytes] = await Promise.all([
        baseline.file.arrayBuffer(),
        candidate.file.arrayBuffer(),
      ]);
      setSourceModels({
        baseline: new Uint8Array(baselineBytes),
        candidate: new Uint8Array(candidateBytes),
      });
      setSaveStatus("idle");
      setSaveError(undefined);
      setExportStatus("idle");
      setExportError(undefined);
      setAppliedAlignment(
        usedAlignment
          ? { kind: "with-evidence", estimate: usedAlignment }
          : undefined,
      );
      setResult(next);
    } catch (reason) {
      if (reason instanceof ComparisonCancelledError) {
        setNotice("Comparison cancelled.");
      } else {
        setError(
          reason instanceof Error
            ? reason.message
            : "Comparison failed safely.",
        );
      }
    } finally {
      setProgress(undefined);
      if (activeRunRef.current === controller) activeRunRef.current = null;
    }
  };

  const cancel = () => {
    activeRunRef.current?.abort();
  };

  const openSessionFile = async (file: File) => {
    activeRunRef.current?.abort();
    activeOpenRunRef.current?.abort();
    const controller = new AbortController();
    activeOpenRunRef.current = controller;
    setError(undefined);
    setNotice(undefined);
    setOpenError(undefined);
    setOpenProgress({ stage: "starting", message: "Reading session file" });
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const opened = await openSession(bytes);
      const report = opened.exchange.bundle.report;
      const baselineModel = report.models.find(
        (model) => model.role === "baseline",
      );
      const candidateModel = report.models.find(
        (model) => model.role === "candidate",
      );
      if (!baselineModel || !candidateModel)
        throw new Error(
          "The session archive is missing a required model entry.",
        );
      const baselineBytes = opened.resources.get(baselineModel.sourcePath);
      const candidateBytes = opened.resources.get(candidateModel.sourcePath);
      if (!baselineBytes || !candidateBytes)
        throw new Error(
          "The session archive is missing a required model resource.",
        );
      const { baseline: baselineModelResult, candidate: candidateModelResult } =
        await reimportSessionModels(
          sessionImportSpecFor(baselineModel, baselineBytes),
          sessionImportSpecFor(candidateModel, candidateBytes),
          setOpenProgress,
          controller.signal,
        );
      setSourceModels({ baseline: baselineBytes, candidate: candidateBytes });
      setSaveStatus("idle");
      setSaveError(undefined);
      setExportStatus("idle");
      setExportError(undefined);
      // The saved-session/report contract carries the candidate's applied
      // `modelToComparison` transform (see `runComparison`'s doc comment),
      // but not the `AlignmentEvidence` that may have produced it -- that
      // evidence lives only in this session's own React state and does not
      // round-trip through a session archive. A reopened session can
      // therefore only show the bare transform, never the residuals,
      // warnings, or convergence evidence a live estimate carries.
      const reopenedCandidateTransform =
        report.analysis.result.candidate.modelToComparison;
      setAppliedAlignment(
        isIdentityTransform(reopenedCandidateTransform)
          ? undefined
          : { kind: "transform-only", transform: reopenedCandidateTransform },
      );
      setResult({
        baseline: baselineModelResult,
        candidate: candidateModelResult,
        analysis: report.analysis.result,
      });
    } catch (reason) {
      if (reason instanceof ComparisonCancelledError) {
        setNotice("Session open cancelled.");
      } else {
        setOpenError(describeSessionFailure(reason, "open"));
      }
    } finally {
      setOpenProgress(undefined);
      if (activeOpenRunRef.current === controller)
        activeOpenRunRef.current = null;
    }
  };

  const handleSaveSession = (summary: ModelComparisonPresentationSummary) => {
    if (!result || !sourceModels || saveInFlightRef.current) return;
    saveInFlightRef.current = true;
    setSaveStatus("saving");
    setSaveError(undefined);
    void (async () => {
      try {
        const saved = await saveSession({
          baseline: result.baseline,
          candidate: result.candidate,
          analysis: result.analysis,
          summary,
          sourceModels,
        });
        downloadSessionArchive(saved);
        setSaveStatus("idle");
      } catch (reason) {
        setSaveStatus("error");
        setSaveError(describeSessionFailure(reason, "save"));
      } finally {
        saveInFlightRef.current = false;
      }
    })();
  };

  // Building and rendering a `Report` (`buildComparisonReport` and
  // `renderReportHtml`) is synchronous and pure; the only asynchronous
  // dependency is the presentation summary the caller already waited for
  // before invoking this. `id` uses a fresh `crypto.randomUUID()` -- unlike
  // a saved session, an interactive export has no determinism requirement,
  // and each export is a distinct document the user is choosing to create
  // right now -- mapped into the contract's entity-id format with the
  // report engine's own `boundedEntityId` helper. `createdAt` is the real
  // wall-clock time: the engine itself never reads a clock, so supplying it
  // here is exactly the caller-side policy `buildComparisonReport` expects,
  // and "when this document was produced" is honest, useful metadata for an
  // exported artifact (unlike a session archive, which must stay
  // byte-identical across resaves; see `session.ts`).
  const handleExportReport = (summary: ModelComparisonPresentationSummary) => {
    if (!result || exportInFlightRef.current) return;
    exportInFlightRef.current = true;
    setExportStatus("exporting");
    setExportError(undefined);
    try {
      const report = buildComparisonReport({
        id: brandId<Report["id"]>(
          boundedEntityId("report", crypto.randomUUID()),
        ),
        createdAt: new Date().toISOString(),
        baseline: result.baseline,
        candidate: result.candidate,
        analysis: result.analysis,
        summary,
      });
      downloadReportHtml(report, renderReportHtml(report));
      setExportStatus("idle");
    } catch (reason) {
      setExportStatus("error");
      setExportError(describeReportFailure(reason));
    } finally {
      exportInFlightRef.current = false;
    }
  };

  if (result)
    return (
      <Suspense
        fallback={
          <div className="workbench-loading" role="status">
            Preparing synchronized 3D views…
          </div>
        }
      >
        {appliedAlignment && (
          <div className="applied-alignment-shell">
            {appliedAlignment.kind === "with-evidence" ? (
              <div className="applied-alignment-banner">
                <p>
                  <strong>Alignment applied.</strong> This comparison placed the
                  candidate using an{" "}
                  {alignmentMethodLabel(
                    appliedAlignment.estimate.evidence.method,
                  ).toLocaleLowerCase("en-US")}{" "}
                  estimate you reviewed and accepted before comparing. This
                  result describes the aligned geometry, not the
                  candidate&rsquo;s original position -- see the evidence below.
                </p>
                <AlignmentEvidencePanel estimate={appliedAlignment.estimate} />
              </div>
            ) : (
              <div className="applied-alignment-banner">
                <p>
                  <strong>Non-identity candidate placement.</strong> This
                  reopened session&rsquo;s candidate carries a placement
                  transform other than identity (shown below), so it may reflect
                  an alignment estimate accepted when the session was originally
                  saved. Session archives store only the applied transform, not
                  the alignment evidence (residuals, iterations, warnings) that
                  may have produced it.
                </p>
                <p className="placement-transform">
                  <strong>Applied transform:</strong>{" "}
                  <code>
                    {appliedAlignment.transform
                      .map(formatAlignmentNumber)
                      .join(" ")}
                  </code>
                </p>
              </div>
            )}
          </div>
        )}
        <Workbench
          baseline={result.baseline}
          candidate={result.candidate}
          analysis={result.analysis}
          sessionPanel={{
            onSave: handleSaveSession,
            status: saveStatus,
            error: saveError,
          }}
          reportPanel={{
            onExport: handleExportReport,
            status: exportStatus,
            error: exportError,
          }}
          onReset={() => {
            setResult(undefined);
            setError(undefined);
            setNotice(undefined);
            setOpenError(undefined);
            setSourceModels(undefined);
            setSaveStatus("idle");
            setSaveError(undefined);
            setExportStatus("idle");
            setExportError(undefined);
            setAppliedAlignment(undefined);
            resetAlignmentState();
          }}
        />
      </Suspense>
    );

  return (
    <div className="page shell">
      <header className="page-hero">
        <span className="eyebrow">Private comparison</span>
        <h1>Start with two models</h1>
        <p>
          Choose a trusted baseline and a candidate revision. Import and
          analysis run in a dedicated browser worker; model data is not
          uploaded.
        </p>
      </header>
      <section
        className="session-open-card"
        aria-labelledby="session-open-title"
      >
        <div className="section-heading">
          <span className="step">Optional</span>
          <h2 id="session-open-title">Reopen a saved session</h2>
          <p>
            Already saved a comparison as a <code>.voxelspy</code> file? Open it
            to restore the workbench directly, with the same models and analysis
            result, without re-running the analysis.
          </p>
        </div>
        <label className="session-open-input" htmlFor="session-open-file">
          <span>
            {openProgress
              ? openProgress.message
              : "Choose a .voxelspy session file"}
          </span>
          <input
            id="session-open-file"
            type="file"
            accept=".voxelspy"
            disabled={Boolean(progress) || Boolean(openProgress)}
            onChange={(event) => {
              const file = event.currentTarget.files?.[0];
              event.currentTarget.value = "";
              if (file) void openSessionFile(file);
            }}
          />
          <span className="button button-secondary" aria-hidden="true">
            Browse this device
          </span>
        </label>
        {openError && (
          <div className="comparison-error" role="alert">
            <strong>Session could not be opened</strong>
            <p>{openError}</p>
          </div>
        )}
      </section>
      <section className="comparison-card" aria-labelledby="choose-title">
        <div className="section-heading">
          <span className="step">Step 01</span>
          <h2 id="choose-title">Choose source files</h2>
          <p>
            Select two supported files to compare immediately using common
            millimetre and right-handed Z-up defaults. If a source uses a
            different frame, adjust its Expert settings first.
          </p>
        </div>
        {!capability.analysisSupported && (
          <div className="comparison-error" role="alert">
            <strong>Local comparison is unavailable in this browser</strong>
            <p>{capability.blockingMessage}</p>
          </div>
        )}
        <div className="file-grid">
          <SourceCard
            role="Baseline"
            selection={baseline}
            update={updateBaseline}
          />
          <SourceCard
            role="Candidate"
            selection={candidate}
            update={updateCandidate}
          />
        </div>
        <section className="method-card" aria-labelledby="method-title">
          <div>
            <span className="eyebrow">Analysis method</span>
            <h3 id="method-title">Sampled surface distance</h3>
          </div>
          <p>
            Approximate, tessellation-dependent comparison at a 0.1 mm distance
            tolerance. Preconditions and uncertainty remain attached to the
            result.
          </p>
          <div className="analysis-capacity">
            <label htmlFor="analysis-memory">
              Analysis RAM allowance
              <output htmlFor="analysis-memory">{analysisMemoryMiB} MiB</output>
            </label>
            <input
              id="analysis-memory"
              type="range"
              min={ANALYSIS_MEMORY_MIN_MIB}
              max={ANALYSIS_MEMORY_MAX_MIB}
              step={ANALYSIS_MEMORY_STEP_MIB}
              value={analysisMemoryMiB}
              onChange={(event) =>
                setAnalysisMemoryMiB(Number(event.currentTarget.value))
              }
              aria-describedby={
                capability.memoryNotes.length > 0
                  ? "analysis-memory-help analysis-memory-recommendation"
                  : "analysis-memory-help"
              }
            />
            <small id="analysis-memory-help">
              This is a ceiling, not preallocated memory. Raising it also gives
              the local worker more compute time; large settings may slow or
              destabilize this tab. The starting value above is a recommendation
              for this device, not a hard limit -- move the slider to use any
              allowance in its range.
            </small>
            {capability.memoryNotes.length > 0 && (
              <small id="analysis-memory-recommendation">
                {capability.memoryNotes.join(" ")}
              </small>
            )}
            {fitEstimate?.likelyExceedsAllowance && (
              <p className="capability" role="status">
                <i />
                Estimated analysis memory need is roughly{" "}
                {fitEstimate.estimatedMiB} MiB for the selected files, above the{" "}
                {analysisMemoryMiB} MiB allowance. This is a rough estimate, not
                a guarantee -- raise the allowance or expect the comparison to
                stop with a resource-budget message.
              </p>
            )}
          </div>
        </section>
        <section
          className="method-card alignment-card"
          aria-labelledby="alignment-title"
        >
          <div>
            <span className="step">Optional</span>
            <h3 id="alignment-title">Align candidate to baseline</h3>
          </div>
          <div className="alignment-card-body">
            <label className="alignment-toggle">
              <input
                type="checkbox"
                checked={alignmentEnabled}
                onChange={(event) => {
                  const checked = event.currentTarget.checked;
                  setAlignmentEnabled(checked);
                  if (!checked) setAcceptedAlignment(undefined);
                }}
              />
              Align the candidate before comparing
            </label>
            <p>
              Off by default. Alignment is an estimate you review and explicitly
              accept &mdash; VoxelSpy never auto-aligns geometry. Enter at least{" "}
              {MIN_CORRESPONDENCES} correspondence point pairs (a point on the
              candidate, in its own local frame, paired with its intended
              counterpart on the baseline, in the baseline&rsquo;s own frame),
              then optionally refine the result with iterative-closest-point
              using the already-selected geometry. Results computed with an
              accepted alignment describe how the aligned geometry compares
              &mdash; not the candidate&rsquo;s original position.
            </p>
            {alignmentEnabled && (
              <>
                <div className="correspondence-table-scroll">
                  <table className="correspondence-table">
                    <thead>
                      <tr>
                        <th scope="col">Point</th>
                        <th scope="col">Candidate X (mm)</th>
                        <th scope="col">Candidate Y (mm)</th>
                        <th scope="col">Candidate Z (mm)</th>
                        <th scope="col">Baseline X (mm)</th>
                        <th scope="col">Baseline Y (mm)</th>
                        <th scope="col">Baseline Z (mm)</th>
                        <th scope="col">
                          <span className="visually-hidden">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {correspondenceRows.map((row, index) => (
                        <tr key={row.key}>
                          <th scope="row">{index + 1}</th>
                          {(["moving", "fixed"] as const).flatMap((side) =>
                            ([0, 1, 2] as const).map((axisIndex) => (
                              <td key={`${side}-${axisIndex}`}>
                                <input
                                  type="number"
                                  step="any"
                                  aria-label={`Point ${index + 1} ${
                                    side === "moving" ? "candidate" : "baseline"
                                  } ${["X", "Y", "Z"][axisIndex]} (mm)`}
                                  value={
                                    Number.isFinite(row[side][axisIndex])
                                      ? row[side][axisIndex]
                                      : ""
                                  }
                                  onChange={(event) =>
                                    updateCorrespondencePoint(
                                      row.key,
                                      side,
                                      axisIndex,
                                      event.currentTarget.valueAsNumber,
                                    )
                                  }
                                />
                              </td>
                            )),
                          )}
                          <td>
                            <button
                              type="button"
                              className="button button-secondary"
                              disabled={
                                correspondenceRows.length <= MIN_CORRESPONDENCES
                              }
                              onClick={() => removeCorrespondenceRow(row.key)}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="alignment-actions">
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={addCorrespondenceRow}
                  >
                    Add point
                  </button>
                  <button
                    type="button"
                    className="button button-primary"
                    disabled={!correspondenceRowsReady(correspondenceRows)}
                    onClick={estimateFromPoints}
                  >
                    Estimate from points
                  </button>
                </div>
                {correspondenceError && (
                  <div className="comparison-error" role="alert">
                    <strong>Alignment estimate could not be computed</strong>
                    <p>{correspondenceError}</p>
                  </div>
                )}
                {alignmentEstimate && (
                  <>
                    <AlignmentEvidencePanel estimate={alignmentEstimate} />
                    <div className="alignment-actions">
                      <button
                        type="button"
                        className="button button-secondary"
                        disabled={!canRefineWithIcp}
                        onClick={() => void refineWithIcp()}
                      >
                        {icpBusy
                          ? "Refining locally…"
                          : "Refine with ICP (optional)"}
                      </button>
                      {icpBusy && (
                        <button
                          type="button"
                          className="button button-secondary"
                          onClick={cancelIcpRefine}
                        >
                          Cancel refinement
                        </button>
                      )}
                      <button
                        type="button"
                        className="button button-primary"
                        disabled={Boolean(acceptedAlignment)}
                        onClick={acceptAlignment}
                      >
                        {acceptedAlignment
                          ? "Alignment accepted"
                          : "Accept this alignment"}
                      </button>
                      {acceptedAlignment && (
                        <button
                          type="button"
                          className="button button-secondary"
                          onClick={discardAlignment}
                        >
                          Discard
                        </button>
                      )}
                    </div>
                    {icpError && (
                      <div className="comparison-error" role="alert">
                        <strong>Refinement could not be computed</strong>
                        <p>{icpError}</p>
                      </div>
                    )}
                    {acceptedAlignment && (
                      <p className="capability capability-ready" role="status">
                        <i />
                        This alignment will be applied to the candidate when you
                        compare.
                      </p>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </section>
        <div className="comparison-status" aria-live="polite">
          <span className={ready ? "status-ready" : ""}>
            {progress?.message ??
              notice ??
              (ready
                ? "Inputs pass capability preflight"
                : !capability.analysisSupported
                  ? "Comparison disabled: see the browser support notice above"
                  : "Choose both source files")}
          </span>
          <div className="actions">
            {progress && (
              <button
                className="button button-secondary"
                type="button"
                onClick={cancel}
              >
                Cancel comparison
              </button>
            )}
            <button
              className="button button-primary"
              type="button"
              disabled={!ready}
              onClick={() => void compare()}
            >
              {progress ? "Comparing locally…" : "Validate and compare"}
            </button>
          </div>
        </div>
        {error && (
          <div className="comparison-error" role="alert">
            <strong>Comparison could not continue</strong>
            <p>{error}</p>
          </div>
        )}
        <p className="boundary-note">
          <strong>Local boundary:</strong> source bytes, normalized geometry,
          and analysis remain in this browser. No network-backed model service
          is part of this workflow.
        </p>
      </section>
    </div>
  );
}
